import React from 'react';
import { FiCloud, FiRefreshCw, FiAlertCircle } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import WeatherBadge from './WeatherBadge';
import { useWeatherRefresh } from '../hooks/useWeatherRefresh';

// Turn the stored weather_fetch_error (when the RPC exposes it) into friendly,
// actionable copy. Falls back to inferring from missing coordinates so the
// notice still makes sense before the RPC backfills the error column.
const friendlyReason = (round) => {
  const raw = (round?.weather_fetch_error || '').toLowerCase();
  if (raw.includes('coordinate')) {
    return "We couldn't pin this course on the map, so there's no forecast yet. Fetching weather will try again.";
  }
  if (raw.includes('coverage')) {
    return 'This course is outside our weather coverage area, so we cannot show a forecast for it.';
  }
  if (raw.includes('no weather data')) {
    return 'The forecast service did not return data for this time. Try fetching again in a moment.';
  }
  if (raw) {
    return 'We could not load the forecast for this round. Try fetching it again.';
  }
  if (!round?.course_latitude || !round?.course_longitude) {
    return "We don't have a forecast for this round yet. Fetch weather to load the latest conditions.";
  }
  return "We don't have a forecast for this round yet. Fetch weather to load the latest conditions.";
};

// Conditions block for an upcoming round. Renders the full WeatherBadge with a
// throttled "Refresh" link when weather is present, otherwise a friendly
// reason plus a "Fetch weather" CTA. Both share the cooldown + freshness gate.
export default function RoundWeatherSection({ round, onRefreshed }) {
  const hasWeather = !!round?.weather_golf_score;
  const coverageBlocked = (round?.weather_fetch_error || '').toLowerCase().includes('coverage');

  const { refresh, isRefreshing, inCooldown, cooldownRemainingSec, isStale, error } = useWeatherRefresh({
    roundId: round?.id,
    weatherUpdatedAt: round?.weather_updated_at,
    onRefreshed,
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-text-primary flex items-center">
          <SafeIcon icon={FiCloud} className="w-5 h-5 mr-2 text-text-muted" />
          Conditions
        </h3>
        {hasWeather && !coverageBlocked && (
          <button
            type="button"
            onClick={refresh}
            disabled={isRefreshing || inCooldown || !isStale}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:text-interactive-hover disabled:text-text-muted disabled:cursor-not-allowed transition-colors"
            title={!isStale ? 'Weather is up to date - refresh available once it is over 10 minutes old' : undefined}
          >
            <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing' : inCooldown ? `Refresh (${cooldownRemainingSec}s)` : 'Refresh'}
          </button>
        )}
      </div>

      {hasWeather ? (
        <WeatherBadge
          wide
          golfScore={round.weather_golf_score}
          golfCategory={round.weather_golf_category}
          temperature={round.weather_temperature}
          windSpeed={round.weather_wind_speed}
          precipitation={round.weather_precipitation}
          precipitationLabel={round.weather_precipitation_label}
          humidity={round.weather_humidity}
          uvIndex={round.weather_uv_index}
          alerts={round.weather_alerts}
          isUnplayable={round.weather_is_unplayable}
          groundCategory={round.weather_ground_category}
          groundRainfall={round.weather_ground_rainfall}
          groundHistoryDays={round.weather_ground_history_days}
          daylightCategory={round.weather_daylight_category}
          daylightScore={round.weather_daylight_score}
          sunrise={round.weather_sunrise}
          sunset={round.weather_sunset}
        />
      ) : (
        <div className="rounded-lg border border-border-subtle bg-surface p-4">
          <div className="flex items-start gap-3">
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-text-muted flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-sm text-text-secondary">{friendlyReason(round)}</p>
              {!coverageBlocked && (
                <button
                  type="button"
                  onClick={refresh}
                  disabled={isRefreshing || inCooldown}
                  className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent text-white text-sm font-semibold hover:bg-interactive-hover disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  {isRefreshing ? 'Fetching' : inCooldown ? `Try again in ${cooldownRemainingSec}s` : 'Fetch weather'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {error && (
        <p className="mt-2 text-xs text-status-error">{error.message}</p>
      )}
    </div>
  );
}
